(function attachFitment(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SaikoSpeedFitment = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createFitmentApi() {
  'use strict';

  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

  function parseYears(value) {
    const match = String(value || '').trim().match(/^(\d{4})(?:\s*-\s*(\d{4}))?$/);
    if (!match) return null;
    const start = Number(match[1]);
    const end = Number(match[2] || match[1]);
    if (start > end) return null;
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  function parseFitmentEntries(values) {
    const rows = Array.isArray(values) ? values : String(values || '').split(/\r?\n/);
    return rows.flatMap((raw) => {
      if (normalize(raw) === 'universal') return [{ universal: true, raw: String(raw).trim() }];
      const parts = String(raw).split('|').map((part) => part.trim());
      const years = parseYears(parts[0]);
      if (!(parts.length === 3 || parts.length === 4) || !years || !parts[1] || !parts[2]) return [];
      return [{ years, make: parts[1], model: parts[2], submodel: parts[3] || '', raw: String(raw).trim() }];
    });
  }

  function parseVehicleCookie(cookieString, cookieName = 'hrllc_vehicle') {
    const pair = String(cookieString || '').split(';').map((part) => part.trim())
      .find((part) => part.startsWith(`${cookieName}=`));
    if (!pair) return null;
    try {
      const data = JSON.parse(decodeURIComponent(pair.slice(cookieName.length + 1)));
      if (!data.year || !data.make || !data.model) return null;
      return {
        year: Number(data.year),
        make: String(data.make),
        model: String(data.model),
        submodel: String(data.trim || data.submodel || ''),
        fitmentCode: String(data.fitment_code || '')
      };
    } catch (error) {
      return null;
    }
  }

  function formatFitmentEntry(entry) {
    if (!entry) return '';
    if (entry.universal === true) return 'Universal';
    const years = Array.isArray(entry.years) ? entry.years : [];
    const yearsLabel = years.length === 0
      ? ''
      : years[0] === years[years.length - 1]
        ? String(years[0])
        : `${years[0]}-${years[years.length - 1]}`;
    return [yearsLabel, entry.make, entry.model, entry.submodel]
      .filter(Boolean)
      .join(' ');
  }

  function resolveFitmentState(entries, vehicle) {
    const universal = entries.find((entry) => entry.universal === true);
    if (universal) return { status: 'universal', matchedFitment: universal };
    if (entries.length === 0) return { status: 'unavailable', matchedFitment: null };
    if (!vehicle) return { status: 'no_vehicle', matchedFitment: null };
    const match = entries.find((entry) => entry.years.includes(Number(vehicle.year))
      && normalize(entry.make) === normalize(vehicle.make)
      && normalize(entry.model) === normalize(vehicle.model)
      && (!entry.submodel || normalize(entry.submodel) === normalize(vehicle.submodel)));
    return match
      ? { status: 'confirmed', matchedFitment: match }
      : { status: 'not_confirmed', matchedFitment: null };
  }

  /*
    The Shop by Vehicle token list, indexed into pickable rows.

    Values arrive in three shapes and all three have to feed a cascade:

      "2022|Honda|Civic Si"        per-year token from the sync
      "2022-2025 Honda Civic Si"   range label, the fallback the sync writes
                                   when a product blows the token list cap
      "2006 Mitsubishi Lancer"     single-year label

    Ranges expand to one row per year. Unlike the browse and home cascades,
    nothing here needs the source string: this picker sets a vehicle, it does
    not apply a filter, so a row only has to name a car.
  */
  function indexVehicleTokens(tokens) {
    const seen = new Set();
    const rows = [];
    const push = (year, make, model) => {
      const key = `${year}|${make}|${model}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ year: String(year), make, model });
    };
    const splitMakeModel = (rest) => {
      const parts = String(rest || '').trim().split(/\s+/).filter(Boolean);
      if (parts.length < 2) return null;
      return { make: parts[0], model: parts.slice(1).join(' ') };
    };

    (Array.isArray(tokens) ? tokens : []).forEach((raw) => {
      const value = String(raw == null ? '' : raw).trim();
      if (!value) return;

      if (value.includes('|')) {
        const parts = value.split('|').map((part) => part.trim());
        if (parts[0] && parts[1] && parts[2]) push(parts[0], parts[1], parts[2]);
        return;
      }

      const range = value.match(/^(\d{4})\s*-\s*(\d{4})\s+(.+)$/);
      if (range) {
        const spread = splitMakeModel(range[3]);
        const start = Number(range[1]);
        const end = Number(range[2]);
        if (!spread || start > end) return;
        for (let year = start; year <= end; year += 1) push(year, spread.make, spread.model);
        return;
      }

      const single = value.match(/^(\d{4})\s+(.+)$/);
      if (single) {
        const one = splitMakeModel(single[2]);
        if (one) push(single[1], one.make, one.model);
      }
    });
    return rows;
  }

  /*
    The fitment record that travels to the order.

    The store sells on a fitment guarantee, so the question the team has to be
    able to answer after the fact is not "what car does this customer have" but
    "what did we tell them about this part, for that car, at the moment they
    bought it". A customer who was told Confirmed is a different conversation
    from one who was shown Not confirmed and ordered anyway.

    So this records what the shopper was SHOWN, not a re-derivation. Statuses
    come from the surface that displayed them: the product page passes its live
    presentation status (which includes the wheel and fastener spec verdicts),
    and browse surfaces pass what their card rendered. Absence is recorded
    explicitly as "No vehicle set" rather than left blank, because a missing
    property is ambiguous (an old order, an unhandled path) while a stated one
    is not.

    Keys are underscore-prefixed: Shopify stores them on the line and shows
    them on the order in the admin, but never renders them to the customer in
    the cart or at checkout. Kevin ratified admin-only on 2026-07-31.
  */
  const VERDICT_LABELS = {
    confirmed: 'Confirmed',
    not_confirmed: 'Not confirmed',
    universal: 'Universal',
    unavailable: 'Fitment not listed',
    no_vehicle: 'No vehicle set',
    spec_match: 'Spec match',
    spec_mismatch: 'Spec mismatch',
    wheels: 'Fits by spec, not by chassis row',
    fastener: 'Fits by spec, not by chassis row'
  };

  function vehicleLabel(vehicle) {
    if (!vehicle) return '';
    return [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel]
      .filter(Boolean)
      .join(' ');
  }

  function orderProperties(options) {
    const settings = options || {};
    const vehicle = settings.vehicle || null;

    // An explicit status wins: it is what the surface actually displayed.
    // Without one, derive it, which is the honest answer for a surface that
    // showed no verdict at all.
    let status = settings.status || '';
    if (!status) {
      if (Array.isArray(settings.rows) && settings.rows.length > 0) {
        status = resolveFitmentState(parseFitmentEntries(settings.rows), vehicle).status;
      } else if (!vehicle) {
        status = 'no_vehicle';
      }
    }

    const label = vehicleLabel(vehicle);
    return {
      '_Fitment vehicle': label || 'No vehicle set',
      '_Fitment verdict': VERDICT_LABELS[status] || (settings.fallbackVerdict || 'Not shown at add')
    };
  }

  return {
    normalize,
    parseYears,
    parseFitmentEntries,
    parseVehicleCookie,
    formatFitmentEntry,
    resolveFitmentState,
    indexVehicleTokens,
    vehicleLabel,
    orderProperties
  };
});
